import { IUserProfile } from '@frontegg/rest-api'
import { Row } from 'react-table'
import {
  AnalyzerAnalysisItem,
  CategoryFramework,
  ChartDataValue,
  ConfiguredCPI,
  CPIManifest,
  HistoryPerformanceData,
  Insight,
  IntegrationConfigSyncStatus,
  NistFunction,
  OnyxiaFunction
} from '../models'
import dayjs from 'dayjs'
import axios from './axios'
import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from 'date-fns'
import unique from 'lodash.uniq'
import theme from 'theme'
import { cssVar, ThemeColorToken } from 'theme/config'

export enum Permission {
  /* User Management */
  'UsersSelfPasswordUpdate' = 'users.self.password.update',
  'UsersSelfProfileView' = 'users.self.profile.view',
  'UsersSelfProfileUpdate' = 'users.self.profile.update',

  /* Questionnaire */
  'QuestionnaireView' = 'questionnaire.view',
  'QuestionnaireUpdate' = 'questionnaire.update',

  /* News Feed */
  'InsightsFeedView' = 'insights.feed.view',

  /* Subscription Management */
  'SubscriptionUpdate' = 'subscription.update',
  'SubscriptionDelete' = 'subscription.delete',
  'UsersProfileView' = 'users.profile.view',
  'UsersProfileUpdate' = 'users.profile.update',
  'UsersInvite' = 'users.invite',
  'UsersRoleUpdate' = 'users.role.update',
  'UsersPasswordUpdate' = 'users.password.update',
  'UsersDelete' = 'users.delete',

  /* CPI Library */
  'CPILibraryView' = 'cpi.library.view',
  'CPIConfigView' = 'cpi.config.view',
  'CPIConfigIntegrationView' = 'cpi.config.integration.view',
  'CPIConfigIntegrationUpdate' = 'cpi.config.integration.update',

  /* CPI Dashboard */
  'DashboardCPIView' = 'dashboard.cpi.view',
  'DashboardCPIConfigView' = 'dashboard.cpi.config.view',
  'DashboardCPIConfigUpdate' = 'dashboard.cpi.config.update',
  'DashboardCPIDataExport' = 'dashboard.cpi.data.export',
  'DashboardViewChange' = 'dashboard.view.change',
  'DashboardViewExport' = 'dashboard.view.export',

  /* Configuration */
  'ConfigGlobalView' = 'config.global.view',
  'ConfigGlobalSecurityFrameworkUpdate' = 'config.global.security_framework.update',
  'ConfigIntegrationsList' = 'config.integrations.list',
  'ConfigIntegrationsConfigView' = 'config.integrations.config.view',
  'ConfigIntegrationsConfigEdit' = 'config.integrations.config.edit',
  'ConfigIntegrationsConfigAdd' = 'config.integrations.config.add',

  /* Audit Log */
  'AuditView' = 'audit.view',

  /* Account Management  */
  'AccountManagementView' = 'account.page.view',

  /* SSM */
  'SSMView' = 'ssm.view',
  'SSMEdit' = 'ssm.edit',

  /* Asset Coverage Analyzer */
  'ACAView' = 'aca.view',
  'ACAEdit' = 'aca.edit',

  /* Reports Templates */
  'ReportsTemplatesEdit' = 'reports.templates.edit',
  'ReportsTemplatesDelete' = 'reports.templates.delete',

  /* Exclusion Rules */
  'ExclusionRulesView' = 'exclusion_rules.view',
  'ExclusionRulesEdit' = 'exclusion_rules.edit',
  'ExclusionRulesDelete' = 'exclusion_rules.delete',

  /* Tickets */
  'TicketsCreate' = 'tickets.create'
}

export enum Role {
  Default = 'default',
  Owner = 'owner',
  Admin = 'admin',
  CPIManager = 'cpi_manager',
  Viewer = 'viewer',
  Demo = 'demo'
}

// The Demo role's display name (as returned by Frontegg and shown in role pickers).
export const DEMO_ROLE_NAME = 'Demo'

export const CPIColor = {
  Healthy: theme.baseColors.info[20],
  Problematic: 'rgba(255, 138, 128, 0.20)',
  Initializing: theme.baseColors.info[30]
}

/**
 * Returns a promise that is resolved after given ms
 * @param ms Milliseconds after which the promise is resolved
 * @returns
 */
export const delay = (ms: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(() => resolve(), ms)
  })
}

/*
 * Capitalize the first letter of a string
 * */
export const capitalizeString = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const customSortByOrder: any = (importOrder: string[]) => {
  return importOrder.reduce((obj, item, index) => {
    return {
      ...obj,
      [item]: index
    }
  }, {})
}

export const sanitizeString = (str: string, char: string, replaceValue: string) => {
  return str.replace(char, replaceValue)
}

export const dateFormat = {
  long: 'MMMM DD, YYYY',
  short: 'MMM DD, YYYY',
  shortUnComma: 'MMM DD YYYY',
  simple: 'MMM DD',
  chartAxis: 'MMM. DD',
  chartAxisRange: 'MMM DD',
  pastYear: "MMM 'YY",
  pastYearLong: "MMMM 'YY",
  day: 'DD',
  historical: 'MM/YY',
  historicalDay: 'MM/DD/YY',
  auditLogDefault: 'MM/DD/YYYY',
  customLabel: 'YYYY-MM-DD',
  customLabelHour: 'YYYY-MM-DD HH:mm:ss',
  lastUpdate: 'ddd, MMM DD YYYY',
  lastUpdateHour: 'MMM DD, YYYY hh:mm A',
  fullYearHour: 'MM/DD/YYYY HH:mm:ss'
}

export const hasPermission = (requested: Permission, user: IUserProfile | null | undefined): boolean => {
  if (!user) {
    return false
  }

  const permissions = user.permissions
  const userHasPermission = permissions.find((n) => n.key === requested)

  return !!userHasPermission
}

export const hasAnyPermission = (requested: Permission[], user: IUserProfile | null | undefined): boolean => {
  if (!user) {
    return false
  }

  const userPermissions = user.permissions
  const userHasPermission = userPermissions.some((n) => requested.includes(n.key as Permission))

  return userHasPermission
}

/* Role keys the user holds in the account they're currently in. A role counts if it's for that
 * account or for none in particular; roles for another account are ignored. No user or no roles
 * yields an empty list, so the checks below deny access by default rather than guessing. */
const activeTenantRoleKeys = (user: IUserProfile | null | undefined): string[] => {
  const roles = user?.roles
  if (!Array.isArray(roles)) {
    return []
  }

  const activeTenantId = user?.tenantId
  return roles
    .filter((role) => role && (!role.tenantId || role.tenantId === activeTenantId))
    .map((role) => role.key)
    .filter((key): key is string => typeof key === 'string')
}

export const hasRole = (requested: Role, user: IUserProfile | null | undefined): boolean =>
  activeTenantRoleKeys(user).includes(requested)

export const hasAnyRole = (requested: Role[], user: IUserProfile | null | undefined): boolean => {
  const userRoleKeys = activeTenantRoleKeys(user)
  return requested.some((role) => userRoleKeys.includes(role))
}

export const isViewerRole = (user: IUserProfile | null | undefined): boolean => hasRole(Role.Viewer, user)

/**
 * Turn a numbered-list string (with possible HTML <div> sub-steps) into an array.
 * The IP list inside step 3 of the “abnormal” integration is preserved.
 */
export const stringOfNumberedListToArray = (input: string, name: string): RegExpMatchArray | null => {
  /* -----------------------------------------------------------------
   * 1. Special handling for the three integrations that already use a
   *    custom regex (they contain <a> tags that must not be broken).
   * ----------------------------------------------------------------- */
  if (['rapid7_vm', 'rapid7_appsec', 'knowbe4_kmsat'].includes(name)) {
    const regex = /(\d+\.\s*(?:(?!<a\s[^>]*>|<\/a>)[\s\S])*?)(?=\d+\.|$)/g
    const parts = input.split(regex).filter((s) => s.trim() !== '')

    if (name === 'rapid7_vm') {
      parts[9] = parts[9] + parts[10]
      parts.splice(-1)
      return parts as any
    }
    if (name === 'rapid7_appsec') {
      parts[10] = parts[10] + parts[11]
      parts.splice(-1)
      return parts as any
    }
    if (name === 'knowbe4_kmsat') {
      parts[6] =
        parts[6] +
        parts[7] +
        parts[8] +
        parts[9] +
        parts[10] +
        parts[11] +
        parts[12] +
        parts[13] +
        parts[14] +
        parts[15] +
        parts[16] +
        parts[17]
      parts.splice(-11)
      return parts as any
    }
  }
  /*
   * This is basically adding support for the span of IP Addresses special case
   * */
  if (name === 'axonius') {
    const stepStartRegex = /^\d+\.\s/gm
    const starts: number[] = []
    let m: RegExpExecArray | null

    while ((m = stepStartRegex.exec(input)) !== null) {
      starts.push(m.index)
    }

    if (starts.length === 0) return input.trim() ? ([input.trim()] as any) : null

    const matches: string[] = []
    for (let i = 0; i < starts.length; i++) {
      const start = starts[i]
      const end = i + 1 < starts.length ? starts[i + 1] : input.length
      matches.push(input.slice(start, end).trim())
    }

    return matches.length > 0 ? (matches as any) : null
  }

  if (name === 'abnormal' || name === 'tanium') {
    const topLevelStepRegex = /^\d+\.\s(?:.*(?:\n\s{3}.*)*)/gm
    const matches: string[] = []
    let match: RegExpExecArray | null

    while ((match = topLevelStepRegex.exec(input)) !== null) {
      // Include everything up to (but not including) the next top-level step
      const start = match.index
      const end = topLevelStepRegex.lastIndex
      const stepText = input.slice(start, end).trim()
      matches.push(stepText)
    }

    // If no matches, fallback to full string
    if (matches.length === 0 && input.trim()) {
      matches.push(input.trim())
    }

    return matches.length > 0 ? (matches as any) : null
  }

  /* -----------------------------------------------------------------
   * 2. Generic case
   * ----------------------------------------------------------------- */
  //  • \d+\.          – the step number followed by a literal dot
  //  • \s*            – optional whitespace after the dot
  //  • (?: … )*?      – non-greedy repeat of anything that is NOT a step start
  //      (?! \d+\. )  – negative lookahead: stop before the next step
  //      (?! $ )      – or the end of the string
  //  • The .*? inside the group is allowed to contain dots **as long as**
  //    they are not followed by a digit that starts a new step.
  const regex = /(\d+\.\s*[\s\S]*?)(?=\d+\.|$)/gs
  return input.match(regex)
}

export const formatCPIDisplayNameForURL = (name: string) => {
  return name.toLowerCase()
}

/*
 * Returns desc = red over green, or asc = green over red
 * */
export const getHealthDirection = (zoneColor: string, lastValue: number, sla: number) => {
  const isPositive = lastValue <= sla
  if (zoneColor === 'green' && isPositive) return 'desc'
  if (zoneColor === 'red' && isPositive) return 'asc'
  if (zoneColor === 'green' && !isPositive) return 'asc'
  if (zoneColor === 'red' && !isPositive) return 'desc'

  return 'desc'
}

export const checkIsGreen = (healthDirection: string, lastValue: number, sla: number) => {
  return (healthDirection === 'desc' && lastValue <= sla) || (healthDirection === 'asc' && lastValue > sla)
}

export const configuredActiveIntegrations = (integrations: any, manifest: any) => {
  const configuredIntegrationNames = integrations.map((item: { name: string }) => item.name)
  return manifest.supported_integrations.filter((item: { name: string }) =>
    configuredIntegrationNames.includes(item.name)
  )
}

export const months = [
  'January',
  'Feburary',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

export const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const getTrendsRangeValues = (data: ChartDataValue[] | HistoryPerformanceData[]): number[] => {
  const arrStart = data[0]
  const arrStartMonthNumber = dayjs(arrStart.xValue).get('month')
  const arrStartMonth = months[arrStartMonthNumber]
  const arrStartYear = dayjs(arrStart.xValue).year()
  const startDate = new Date(`${arrStartMonth}/01/${arrStartYear}`)

  const arrEndValue = data[data.length - 1].xValue
  const daysInLastMonth = dayjs(arrEndValue).daysInMonth()
  const arrEndMonthNumber = dayjs(arrEndValue).get('month')
  const arrEndYear = dayjs(arrEndValue).year()
  const arrEndMonth = months[arrEndMonthNumber]
  const endDate = new Date(`${arrEndMonth}/${daysInLastMonth}/${arrEndYear}`)

  const s = new Date(startDate).getTime()
  const e = new Date(endDate).getTime()

  return [s, e]
}

export const getFirstLastWeekDay = (startDate: string, endDate: string) => {
  const n1 = dayjs(startDate).startOf('w')
  const n2 = dayjs(n1).add(1, 'day')
  const k1 = dayjs(n2).format(dateFormat.short)

  const n3 = dayjs(endDate).endOf('w')
  const n4 = dayjs(n3).add(1, 'day')
  const k2 = dayjs(n4).format(dateFormat.short)

  return [k1, k2]
}

export const removeDuplicates = (data: any) => {
  return data.filter((value: any, index: number) => data.indexOf(value) === index)
}

export const cpiCategoryColors: Record<string, { background: string; color: string }> = {
  'Threat Detection': {
    background: 'rgba(177, 92, 15, 1)',
    color: theme.colors.white
  },
  'Vulnerability Management': {
    background: 'rgba(125, 117, 7, 1)',
    color: theme.colors.white
  },
  'Training & Awareness': {
    background: theme.baseColors.primary[30],
    color: theme.colors.white
  },
  'Cloud Security': {
    background: 'rgba(250, 195, 100, 1)',
    color: theme.colors.primaryDark
  },
  'Identity & Access Management': {
    background: 'rgba(142, 154, 255, 1)',
    color: theme.colors.primaryDark
  },
  'Network Security': {
    background: 'rgba(71, 116, 130, 1)',
    color: theme.colors.white
  }
}

export const getCpiCategoryColors = (
  category: string,
  framework: CategoryFramework = CategoryFramework.Onyxia
): { background: string; color: string } => {
  const defaultColors = {
    background: cssVar('category-chip-default-bg'),
    color: cssVar('category-chip-default-text')
  }

  const colorForReport = FunctionColorsForReports[framework][category]
  if (colorForReport) {
    return { background: colorForReport.category_background, color: colorForReport.category_color }
  }

  return defaultColors
}

// Theme-aware category colors for chips, dots and the framework donut; reports keep the saturated
// palette via getCpiCategoryColors above. Dark values match that palette.
const onyxiaCategoryChipTokens: Record<string, { bg: ThemeColorToken; text: ThemeColorToken }> = {
  [OnyxiaFunction.DetectionAndRespond]: { bg: 'cat-chip-detection-and-response-bg', text: 'cat-chip-text-inverted' },
  [OnyxiaFunction.VulnerabilityManagement]: { bg: 'cat-chip-vulnerability-management-bg', text: 'cat-chip-text-inverted' },
  [OnyxiaFunction.TrainingAndAwareness]: { bg: 'cat-chip-training-and-awareness-bg', text: 'cat-chip-text-inverted' },
  [OnyxiaFunction.CloudSecurity]: { bg: 'cat-chip-cloud-security-bg', text: 'category-chip-default-text' },
  [OnyxiaFunction.IAM]: { bg: 'cat-chip-iam-bg', text: 'category-chip-default-text' },
  [OnyxiaFunction.NetworkSecurity]: { bg: 'cat-chip-network-security-bg', text: 'cat-chip-text-inverted' },
  [OnyxiaFunction.DeviceManagement]: { bg: 'cat-chip-device-management-bg', text: 'category-chip-default-text' },
  [OnyxiaFunction.MailAndWebSecurity]: { bg: 'cat-chip-mail-and-web-security-bg', text: 'cat-chip-text-inverted' },
  [OnyxiaFunction.ApplicationSecurity]: { bg: 'cat-chip-application-security-bg', text: 'category-chip-default-text' }
}

export const getOnyxiaCategoryChipBg = (category: string): string | undefined => {
  const tokens = onyxiaCategoryChipTokens[category]
  return tokens ? cssVar(tokens.bg) : undefined
}

export const getCpiCategoryChipColors = (
  category: string,
  framework: CategoryFramework = CategoryFramework.Onyxia
): { background: string; color: string } => {
  const tokens = onyxiaCategoryChipTokens[category]
  if (tokens) {
    return { background: cssVar(tokens.bg), color: cssVar(tokens.text) }
  }

  return getCpiCategoryColors(category, framework)
}

// Custom-framework chips store a dark rgba per category (from the 12-slot palette in Categories.tsx).
// Map it to a theme-aware token (dark byte-identical, light per design); non-slot values pass through.
const customChipBgTokens: Record<string, ThemeColorToken> = {
  'rgba(255,235,249,1)': 'custom-cat-chip-1-bg',
  'rgba(255,224,250,1)': 'custom-cat-chip-2-bg',
  'rgba(255,203,253,1)': 'custom-cat-chip-3-bg',
  'rgba(255,169,252,1)': 'custom-cat-chip-4-bg',
  'rgba(236,133,236,1)': 'custom-cat-chip-5-bg',
  'rgba(206,107,207,1)': 'custom-cat-chip-6-bg',
  'rgba(176,81,179,1)': 'custom-cat-chip-7-bg',
  'rgba(148,55,152,1)': 'custom-cat-chip-8-bg',
  'rgba(127,40,131,1)': 'custom-cat-chip-9-bg',
  'rgba(112,21,119,1)': 'custom-cat-chip-10-bg',
  'rgba(92,7,99,1)': 'custom-cat-chip-11-bg',
  'rgba(77,0,83,1)': 'custom-cat-chip-12-bg'
}

export const resolveCustomChipBg = (bg?: string): string => {
  if (!bg) return bg || ''
  const token = customChipBgTokens[bg.replace(/\s/g, '')]
  return token ? cssVar(token) : bg
}

export const resolveCustomChipText = (text?: string): string => {
  if (!text) return text || ''
  const normalized = text.replace(/\s/g, '')
  if (normalized === 'rgba(255,255,255,1)') return cssVar('cat-chip-text-inverted')
  if (normalized === 'rgba(0,0,0,1)') return cssVar('custom-cat-chip-text-black')
  return text
}

export const OnyxiaCategoryColorsForReports: Record<string, { category_background: string; category_color: string }> = {
  [OnyxiaFunction.DetectionAndRespond]: {
    category_background: 'rgba(177, 92, 15, 1)',
    category_color: theme.colors.white
  },
  [OnyxiaFunction.VulnerabilityManagement]: {
    category_background: 'rgba(125, 117, 7, 1)',
    category_color: theme.colors.white
  },
  [OnyxiaFunction.TrainingAndAwareness]: {
    category_background: theme.baseColors.primary[30],
    category_color: theme.colors.white
  },
  [OnyxiaFunction.CloudSecurity]: {
    category_background: 'rgba(250, 195, 100, 1)',
    category_color: theme.colors.primaryDark
  },
  [OnyxiaFunction.IAM]: {
    category_background: 'rgba(142, 154, 255, 1)',
    category_color: theme.colors.primaryDark
  },
  [OnyxiaFunction.NetworkSecurity]: {
    category_background: 'rgba(71, 116, 130, 1)',
    category_color: theme.colors.white
  },
  [OnyxiaFunction.DeviceManagement]: {
    category_background: 'rgba(219, 219, 219, 1)',
    category_color: theme.colors.primaryDark
  },
  [OnyxiaFunction.MailAndWebSecurity]: {
    category_background: '#072845',
    category_color: theme.colors.white
  },
  [OnyxiaFunction.ApplicationSecurity]: {
    category_background: '#D279DF',
    category_color: theme.colors.primaryDark
  }
}

export const NistFunctionColorsForReports: Record<string, { category_background: string; category_color: string }> = {
  [NistFunction.Detect.toLowerCase()]: {
    category_background: 'rgba(255, 222, 106,1)',
    category_color: theme.colors.primaryDark
  },
  [NistFunction.Govern.toLowerCase()]: {
    category_background: 'rgba(255, 158, 71, 1)',
    category_color: theme.colors.white
  },
  [NistFunction.Identify.toLowerCase()]: {
    category_background: 'rgb(142, 154, 255,1)',
    category_color: theme.colors.primaryDark
  },
  [NistFunction.Protect.toLowerCase()]: {
    category_background: 'rgba(199, 126, 218,1)',
    category_color: theme.colors.primaryDark
  },
  [NistFunction.Recover.toLowerCase()]: {
    category_background: 'rgba(153, 253, 192, 1)',
    category_color: theme.colors.primaryDark
  },
  [NistFunction.Respond.toLowerCase()]: {
    category_background: 'rgba(255, 127, 107,1)',
    category_color: theme.colors.primaryDark
  }
}

export const FunctionColorsForReports = {
  [CategoryFramework.Onyxia]: OnyxiaCategoryColorsForReports,
  [CategoryFramework.NIST]: NistFunctionColorsForReports,
  [CategoryFramework.Custom]: OnyxiaCategoryColorsForReports
}

export const extractSeverityValues = (cpiConfig: ConfiguredCPI | null, manifest: CPIManifest | null) => {
  const values: any = {}

  if (cpiConfig) {
    if (cpiConfig.user_input.visualization_param.identical_section_values) {
      values.sla = cpiConfig.user_input.visualization_param.identical_value
      values.isNoSeveritiesCpi = true
      if (manifest?.ui.trend_chart.user_input.visualization_param.sections) {
        manifest.ui.trend_chart.user_input.visualization_param.sections.forEach(({ name, value }) => {
          values[name] = value
        })
      }
    }
    if (
      !cpiConfig.user_input.visualization_param.identical_section_values &&
      cpiConfig.user_input.visualization_param.sections
    ) {
      cpiConfig.user_input.visualization_param.sections.forEach(({ name, value }) => {
        values[name] = value
      })
    }
    cpiConfig.user_input.formula_params?.forEach((param) => {
      if (param.sections.length > 0) {
        param.sections.forEach(({ name, value }) => {
          values[name] = value
        })
      } else if (param.value) {
        values[param.name] = param.value
      }
    })
  } else if (manifest) {
    if (manifest.ui.trend_chart.user_input.visualization_param.identical_section_values) {
      values.sla = manifest.ui.trend_chart.user_input.visualization_param.identical_value
      values.isNoSeveritiesCpi = true
      manifest.ui.trend_chart.user_input.visualization_param.sections.forEach(({ name, value }) => {
        values[name] = value
      })
    }
    if (!manifest.ui.trend_chart.user_input.visualization_param.identical_section_values) {
      manifest.ui.trend_chart.user_input.visualization_param.sections.forEach(({ name, value }) => {
        values[name] = value
      })
    }
    manifest.ui.trend_chart.user_input.formula_params?.forEach((param) => {
      if (param.sections.length > 0) {
        param.sections.forEach(({ name, value }) => {
          values[name] = value
        })
      } else if (param.value) {
        values[param.name] = param.value
      }
    })
  }

  return values
}

export function alphanumericSort<T extends AnalyzerAnalysisItem>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string
): 0 | 1 | -1 {
  const valueA = (rowA as any).original[columnId]
  const valueB = (rowB as any).original[columnId]

  if (!isNaN(valueA) || !isNaN(valueB)) {
    if (valueA > valueB) return 1
    if (valueB > valueA) return -1
    return 0
  } else {
    if (valueA.toLowerCase() > valueB.toLowerCase()) return 1
    if (valueB.toLowerCase() > valueA.toLowerCase()) return -1
    return 0
  }
}

export function chronologicalSort<T extends AnalyzerAnalysisItem>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string
): number {
  const valueA = (rowA as any).original[columnId]
  const valueB = (rowB as any).original[columnId]

  return +new Date(valueA) - +new Date(valueB)
}

export enum AllCPIsReportType {
  GENERAL = 'general',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly'
}

export const breadcrumbs = {
  profile: [{ name: 'Your Profile' }],
  userManagement: [{ name: 'User Management' }],
  integrations: [{ name: 'Integrations' }],
  auditLog: [{ name: 'Audit Logs' }],
  accountManagement: [{ name: 'Account Settings' }],
  frameworksConfiguration: [{ name: 'Frameworks Configuration' }],
  SSM: [{ name: 'Security Stack Map' }],
  ACA: [{ name: 'Asset Coverage Analyzer' }],
  insights: [{ name: 'Insights' }],
  library: [{ name: 'Cyber Performance Indicator (CPI) Library' }],
  dashboard: [{ name: 'Dashboard' }]
}

/* A custom framework's cyFramework is its id, prefixed with "custom_". Built-in
 * frameworks (onyxia, nist, hipaa, pci, soc2) are not custom. Guards calls to
 * /api/v3/frameworks/custom, which 404s for non-custom tenants. */
export const isCustomFramework = (cyFramework?: string | null): boolean =>
  !!cyFramework && cyFramework.startsWith('custom_')

export const fetchSyncStatus = async (integrationId: string): Promise<IntegrationConfigSyncStatus | null> => {
  if (!integrationId) return null

  const res = await axios.get<{ data: IntegrationConfigSyncStatus }>(
    `/api/v3/integrations/config/${integrationId}/status`
  )

  if (res.status.toString().includes('20')) {
    return res.data.data
  }

  return null
}

export const equalPiecesGen = function* (count: number, value: number) {
  let mod = value % count
  const weight = (value - mod) / count
  for (let i = 0; i < count; i++) {
    yield mod > 0 ? weight + 1 : weight
    mod--
  }

  return weight
}
export const equalWeightsGen = function* (count: number) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  yield* equalPiecesGen(count, 100)
}

export const formatNumberCommas = (x: number | string) => {
  return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
}

export const camelToSnake = (key: string) => {
  const result = key.replace(/([A-Z])/g, ' $1')

  return result.split(' ').join('_').toLowerCase()
}

// date-fns' week/month helpers use local calendar components; reinterpreting a value's
// UTC components as local keeps bucketing on the same UTC day the card displays.
export const utcAsLocalDate = (value: Date | string): Date => {
  const d = new Date(value)
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds())
}

export const groupInsightsByDate = (data: Insight[], sortDirection: string): Insight[][] => {
  const thisWeek: Insight[] = []
  const pastWeek: Insight[] = []
  const pastMonth: Insight[] = []
  const pastYear: Insight[] = []

  const now = utcAsLocalDate(new Date())

  data.forEach((d) => {
    if (d.data) {
      const date = utcAsLocalDate(d.created_at || '')
      const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 })
      const endOfWeekDate = endOfWeek(date, { weekStartsOn: 1 })

      const isBetweenToday = dayjs(now).isBetween(startOfWeekDate, endOfWeekDate)

      if (isBetweenToday) {
        thisWeek.push(d)
      } else {
        const lastWeekAnchor = dayjs(now).subtract(7, 'days').toDate()
        const lastWeekStart = startOfWeek(lastWeekAnchor, { weekStartsOn: 1 })
        const lastWeekEnd = endOfWeek(lastWeekAnchor, { weekStartsOn: 1 })
        const isBetweenLastWeek = dayjs(date).isBetween(lastWeekStart, lastWeekEnd)

        if (isBetweenLastWeek) {
          pastWeek.push(d)
        } else {
          const startOfMonthDate = startOfMonth(date)
          const endOfMonthDate = endOfMonth(date)

          const isBetweenLastMonth = dayjs(now).isBetween(startOfMonthDate, endOfMonthDate)

          if (isBetweenLastMonth) {
            pastMonth.push(d)
          } else {
            pastYear.push(d)
          }
        }
      }
    }
  })

  const result: Insight[][] = [thisWeek, pastWeek, pastMonth, pastYear]

  return sortDirection === 'DESC' ? result : result.reverse()
}

const groupInsightsByCpiId = (data: Insight[], sortDirection: string) => {
  const firstDirection = sortDirection === 'DESC' ? 1 : -1
  const secondDirection = firstDirection === 1 ? -1 : 1
  const groups = unique(
    data.map((n) => (n.type === 'ssm_insight' ? 'No CPI' : (n.data?.cpi_display_name || '').slice(-3)))
  ).sort()
  const result = groups
    .map((group) =>
      data.filter((dataItem) =>
        dataItem.type === 'ssm_insight'
          ? group === 'No CPI'
          : (dataItem.data?.cpi_display_name || '').slice(-3) === group
      )
    )
    .map((res) =>
      res.sort((a, b) =>
        new Date(a.created_at || '') > new Date(b.created_at || '') ? firstDirection : secondDirection
      )
    )

  return sortDirection === 'DESC' ? result : result.reverse()
}

const groupInsightsByType = (data: Insight[], sortDirection: string) => {
  const firstDirection = sortDirection === 'DESC' ? 1 : -1
  const secondDirection = firstDirection === 1 ? -1 : 1
  const groups = unique(data.map((n) => n.data?.header || '')).sort()
  const result = groups
    .map((group) => data.filter((dataItem) => (dataItem.data?.header || '') === group))
    .map((res) =>
      res.sort((a, b) =>
        new Date(a.created_at || '') > new Date(b.created_at || '') ? firstDirection : secondDirection
      )
    )

  return sortDirection === 'DESC' ? result : result.reverse()
}

export const groupInsights = (data: Insight[], sortBy: string, sortDirection: string): Insight[][] => {
  if (sortBy === 'insight_date' || sortBy === 'created_at') {
    return groupInsightsByDate(data, sortDirection)
  }
  if (sortBy === 'cpi_name') {
    return groupInsightsByCpiId(data, sortDirection)
  }
  if (sortBy === 'type') {
    return groupInsightsByType(data, sortDirection)
  }

  return [data]
}

export const formatCpiNameToDisplayName = (name: string): string => {
  return `CPI-${name.slice(3)}`
}

export const formatCpiUrlToName = (urlName: string) => {
  return `cpi${urlName.slice(-3)}`
}

export const getIntegrationLogo = (name: string, type?: string) => {
  const lcName = name.toLowerCase()

  if (lcName.includes('abnormal')) {
    return '/icons/rounded/abnormalsecurity.png'
  }
  if (type === 'rounded') {
    return `/icons/rounded/${name}_icon.svg`
  }
  if (type === 'square') {
    if (lcName.includes('rapid7')) {
      return `/icons/square/rapid7_square.png`
    }
    return `/icons/square/${name}_icon.svg`
  }
  if (name) {
    return `/icons/circle/${name}_icon.svg`
  }

  return '/icons/circle/crowdstrike_falcon_icon.svg'
}

/* Resolves an integration instance's circle logo, preferring the manifest's authoritative
 * logo_path (the same field the ACA data-source dropdown uses) and falling back to name-based
 * reconstruction when it isn't populated. Prevents logo mismatches for integrations whose
 * logo_path differs from `${integration_name}_icon.svg` (e.g. Armis Centrix). */
export const getInstanceCircleLogo = (instance?: { integration_name?: string; logo_path?: string } | null) => {
  if (instance?.logo_path) {
    return `/icons/circle/${instance.logo_path}`
  }

  return getIntegrationLogo(instance?.integration_name || '', 'circle')
}

export const getIntegrationLogoSSM = (urlPath: string) => {
  const logoHost = process.env.REACT_APP_LOGO_HOST
  const url = `${logoHost}/${urlPath}`

  return urlPath ? url : 'default-vendor-logo.svg'
}

export const getVendorLogo = (urlPath: string) => {
  if (urlPath.includes('https://')) return urlPath

  const logoHost = process.env.REACT_APP_LOGO_HOST
  const url = `${logoHost}/${urlPath}`

  return urlPath ? url : 'default-vendor-logo.svg'
}

export const getIntegrationLogoNoSuffix = (name: string) => {
  if (name.includes('singularity')) {
    return name
  }
  if (name) {
    return `/icons/circle/${name}`
  }

  return '/icons/circle/crowdstrike_falcon_icon.svg'
}

export const getOrdinalWord = (index: number): string => {
  // Handle negative numbers or invalid input
  if (index < 0 || !Number.isInteger(index)) {
    return ''
  }

  // Common ordinal words for 0-9
  const ordinalWords = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth']

  // Return directly if index is within 0-9
  if (index < ordinalWords.length) {
    return ordinalWords[index]
  }

  // For numbers >= 10, construct ordinal (e.g., 11 -> "eleventh")
  const suffixes = ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th']
  const lastDigit = index % 10
  const lastTwoDigits = index % 100

  // Special case for 11-13 which all end in "th"
  if (lastTwoDigits === 11 || lastTwoDigits === 12 || lastTwoDigits === 13) {
    return `${index}th`
  }

  return `${index}${suffixes[lastDigit]}`
}

// Escapes regex metacharacters so user-typed search terms can be embedded in a RegExp literally.
export const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
